import { friendlyError } from "@/lib/friendly-errors";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import {
  Download,
  Trash2,
  ShieldCheck,
  AlertTriangle,
  UserCog,
  CreditCard,
  Ban,
} from "lucide-react";

import {
  exportMyData,
  deleteMyAccount,
  getMyDeletionEligibility,
} from "@/lib/privacy.functions";
import { secureSignOut } from "@/lib/secure-sign-out";
import { useIsNativeApp } from "@/lib/native-app";

export const Route = createFileRoute("/account/privacy")({
  head: () => ({
    meta: [
      { title: "Your Privacy & Data — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Export your data, correct your information, or delete your Recovery Trauma™ account.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AccountPrivacyPage,
});

function AccountPrivacyPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmStep, setConfirmStep] = useState(false);
  const [err, setErr] = useState("");
  const [checkingBilling, setCheckingBilling] = useState(false);
  const nativeApp = useIsNativeApp();
  const [billingBlock, setBillingBlock] = useState<{
    provider: "stripe" | "paypal" | "apple" | null;
    access_ends_at: string | null;
  } | null>(null);
  const [appleNotice, setAppleNotice] = useState<{
    access_ends_at: string | null;
  } | null>(null);
  const [cancelScheduled, setCancelScheduled] = useState<{
    access_ends_at: string | null;
  } | null>(null);

  const exportFn = useServerFn(exportMyData);
  const deleteFn = useServerFn(deleteMyAccount);
  const eligibilityFn = useServerFn(getMyDeletionEligibility);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(async ({ data, error }) => {
      if (!mounted) return;
      if (!data.user) {
        const { readStoredAuthSession } = await import("@/lib/auth-session");
        // A failed lookup is not proof of being signed out.
        if (error && readStoredAuthSession()?.user?.id) return;
        navigate({ to: "/login", search: { redirect: "/account/privacy" } });
        return;
      }
      setAuthed(true);
      setCheckingAuth(false);
    });
    return () => {
      mounted = false;
    };
  }, [navigate]);

  if (checkingAuth || !authed) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center text-sm text-muted-foreground">
        Loading your privacy controls…
      </div>
    );
  }

  async function handleExport() {
    setErr("");
    setExporting(true);
    try {
      const { json } = await exportFn();
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recovery-trauma-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(friendlyError(e, "Export failed."));
    } finally {
      setExporting(false);
    }
  }

  function formatDate(iso: string | null) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  /**
   * Gate the delete flow on live billing state before showing the
   * confirmation step, so nobody deletes an account that is still billing.
   */
  async function handleContinueToDelete() {
    setErr("");
    setBillingBlock(null);
    setAppleNotice(null);
    setCancelScheduled(null);
    setCheckingBilling(true);
    try {
      const e = await eligibilityFn();
      if (!e.can_delete) {
        setBillingBlock({ provider: e.provider, access_ends_at: e.access_ends_at });
        return;
      }
      if (e.provider === "apple") {
        // Apple owns this subscription. Deletion stays available — we warn
        // rather than block, because Recovery Trauma™ cannot cancel with Apple.
        setAppleNotice({ access_ends_at: e.access_ends_at });
      }
      if (e.cancel_at_period_end) {
        setCancelScheduled({ access_ends_at: e.access_ends_at });
      }
      setConfirmStep(true);
    } catch (e) {
      setErr(friendlyError(e, "Could not check your membership status."));
    } finally {
      setCheckingBilling(false);
    }
  }

  async function handleDelete() {
    setErr("");
    setDeleting(true);
    try {
      await deleteFn();
      await secureSignOut(queryClient);
      navigate({ to: "/", replace: true });
    } catch (e) {
      setErr(friendlyError(e, "Delete failed."));
      setDeleting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" /> Your data, your rights
        </div>
        <h1 className="font-serif text-3xl tracking-tight text-foreground md:text-4xl">
          Privacy & Data
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
          Under UK GDPR and applicable US state laws you can access, correct,
          export, or delete your data at any time.
        </p>
      </header>

      <div className="space-y-5">
        <section className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-3 flex items-center gap-2">
            <UserCog className="h-5 w-5 text-[var(--gold-deep)]" />
            <h2 className="font-serif text-xl text-foreground">Correct your information</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Update your display name, avatar, and preferences in your profile.
          </p>
          <a
            href="/profile"
            className="mt-3 inline-flex items-center justify-center rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
          >
            Open profile
          </a>
        </section>

        <section className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-3 flex items-center gap-2">
            <Download className="h-5 w-5 text-[var(--gold-deep)]" />
            <h2 className="font-serif text-xl text-foreground">Export your data</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Download a JSON copy of everything we store about you — profile,
            journal entries, dream journal, check-ins, community activity,
            saved exercises, and subscription preferences.
          </p>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {exporting ? "Preparing your file…" : "Export my data (.json)"}
          </button>
        </section>

        <BlockedMembersSection />



        <section className="rounded-2xl border border-destructive/30 bg-card p-6">
          <div className="mb-3 flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            <h2 className="font-serif text-xl text-foreground">Delete your account</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            This permanently removes your sign-in, profile, journal and dream
            entries, check-ins, assessment answers, saved items, exercise
            history and your community posts, comments and hearts. This action
            cannot be undone.
          </p>
          <div className="mt-3 rounded-xl border border-border bg-muted/40 p-4">
            <p className="text-sm font-medium text-foreground">
              What is kept, and why
            </p>
            <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              <li>
                <strong className="font-medium text-foreground">
                  Support messages
                </strong>{" "}
                you sent us, so we have a record of any issue that was raised.
              </li>
              <li>
                <strong className="font-medium text-foreground">
                  Safety reports
                </strong>{" "}
                made by or about you, so moderation decisions stay accountable.
              </li>
              <li>
                <strong className="font-medium text-foreground">
                  Your mailing-list record
                </strong>
                , marked unsubscribed, so you are not emailed again by mistake.
              </li>
              <li>
                <strong className="font-medium text-foreground">
                  Payment and invoice records
                </strong>{" "}
                held by our payment providers, which we must keep for tax and
                accounting law.
              </li>
            </ul>
            <p className="mt-2 text-xs text-muted-foreground">
              You can ask us to review anything kept by emailing{" "}
              info@recoverytrauma.com.
            </p>
          </div>

          {billingBlock && (
            <div className="mt-4 rounded-xl border border-[var(--gold-deep)]/40 bg-[var(--gold-deep)]/5 p-4">
              <div className="flex items-start gap-2">
                <CreditCard className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
                <div>
                  <p className="text-sm text-foreground">
                    You have an active membership. Before deleting your account,
                    you must cancel your membership to prevent future charges.
                  </p>
                  {billingBlock.access_ends_at && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Your next billing date is{" "}
                      {formatDate(billingBlock.access_ends_at)}.
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href={nativeApp ? "/my-membership" : "/billing?cancel=1"}
                  data-testid="delete-gate-cancel-link"
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  {nativeApp ? "Go to My Membership to cancel" : "Go to Membership & Billing"}
                </a>
                <button
                  onClick={() => setBillingBlock(null)}
                  className="min-h-[44px] rounded-lg border border-input bg-background px-4 text-sm font-medium text-foreground hover:bg-accent"
                >
                  Keep Account
                </button>
              </div>
            </div>
          )}

          {appleNotice && (
            <div className="mt-4 rounded-xl border border-[var(--gold-deep)]/40 bg-[var(--gold-deep)]/5 p-4 text-sm text-foreground">
              <p className="font-medium">
                You have an active subscription managed by Apple.
              </p>
              <p className="mt-1">
                Deleting your Recovery Trauma™ account does not cancel your
                Apple subscription. Please manage your subscription through
                your Apple account if you want to stop future renewals.
              </p>
              <a
                href="https://apps.apple.com/account/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center justify-center rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
              >
                Manage Subscription with Apple
              </a>
            </div>
          )}

          {cancelScheduled && (
            <div className="mt-4 rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
              Your membership has been cancelled successfully. You can continue
              using your membership until the end of your current billing period
              {cancelScheduled.access_ends_at
                ? ` (${formatDate(cancelScheduled.access_ends_at)})`
                : ""}
              . After that, you may delete your account.
            </div>
          )}

          {!confirmStep ? (
            <button
              onClick={handleContinueToDelete}
              disabled={checkingBilling}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg border border-destructive bg-background px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-60"
            >
              {checkingBilling ? "Checking your membership…" : "Continue to delete"}
            </button>
          ) : (
            <div className="mt-4 rounded-xl border border-destructive/40 bg-destructive/5 p-4">
              <div className="mb-2 flex items-start gap-2 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  Type <strong>DELETE</strong> to confirm. This will sign you
                  out and remove your data.
                </p>
              </div>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-destructive"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setConfirmStep(false);
                    setConfirmText("");
                  }}
                  disabled={deleting}
                  className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting || confirmText !== "DELETE"}
                  className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Permanently delete my account"}
                </button>
              </div>
            </div>
          )}
        </section>

        {err && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {err}
          </div>
        )}
      </div>
    </div>
  );
}

type BlockedMember = {
  blocked_user_id: string;
  display_name: string;
  created_at: string;
};

/**
 * Lets a member see and undo the community blocks they have made.
 * Required alongside the in-feed block action for App Store guideline 1.2.
 */
function BlockedMembersSection() {
  const [rows, setRows] = useState<BlockedMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Cast: these functions are newer than the generated Supabase types.
  // Keep `supabase` as the receiver — a detached `supabase.rpc` throws.
  const callRpc = (fn: string, args?: Record<string, string>) =>
    (
      supabase.rpc as unknown as (
        f: string,
        a?: Record<string, string>,
      ) => Promise<{ data: unknown; error: unknown }>
    ).call(supabase, fn, args);

  async function load() {
    try {
      const { data, error } = await callRpc("my_blocked_members");
      setRows(error ? [] : ((data ?? []) as BlockedMember[]));
    } catch {
      setRows([]);
    }
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  async function unblock(id: string) {
    setBusyId(id);
    try {
      await callRpc("unblock_member", { p_blocked_user_id: id });
      await load();
    } catch {
      /* leave the list as-is; the member can retry */
    }
    setBusyId(null);
  }

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="mb-3 flex items-center gap-2">
        <Ban className="h-5 w-5 text-[var(--gold-deep)]" />
        <h2 className="font-serif text-xl text-foreground">
          Members you've blocked
        </h2>
      </div>
      <p className="text-sm text-muted-foreground">
        You and a blocked member no longer see each other's posts or replies in
        the community. You can undo this at any time.
      </p>

      {loading ? (
        <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          You haven't blocked anyone.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((r) => (
            <li
              key={r.blocked_user_id}
              className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background px-3 py-2"
            >
              <span className="text-sm text-foreground">{r.display_name}</span>
              <button
                type="button"
                onClick={() => unblock(r.blocked_user_id)}
                disabled={busyId === r.blocked_user_id}
                className="inline-flex min-h-10 items-center rounded-lg border border-input bg-card px-3 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-60"
              >
                {busyId === r.blocked_user_id ? "Unblocking…" : "Unblock"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
